import React, { useEffect, useRef, useState } from "react";

/**
 * FirePill — wraps any inline element (typically the BID button) and
 * shoots a continuous burst of golden firework-style sparks from behind
 * it whenever `secondsLeft <= igniteAt`. The sparks fan out in all
 * directions (slightly biased upward), arc gently under gravity, leave
 * short motion-blur trails, and fade to nothing — exactly the look of
 * a sparkler / Roman-candle behind the button.
 *
 * No fire, no glow halo, no shake — just sparks. Outside the burn
 * window the canvas isn't mounted so there's zero render cost.
 *
 *   <FirePill secondsLeft={timer}> <Button>BID</Button> </FirePill>
 */
export default function FirePill({
  secondsLeft,
  igniteAt = 7,
  className = "",
  muted = false,
  children,
}) {
  const burning =
    typeof secondsLeft === "number" && secondsLeft >= 0 && secondsLeft <= igniteAt;
  const intensity = burning
    ? Math.max(0, Math.min(1, (igniteAt - secondsLeft) / igniteAt))
    : 0;
  // ≤4s: subtle shake. ≤2s: harder shake. =0: explosion burst.
  const shaking = burning && secondsLeft <= 4 && secondsLeft > 0;
  const hardShake = burning && secondsLeft <= 2 && secondsLeft > 0;
  const explodeKey = burning && secondsLeft === 0 ? secondsLeft : null;

  // Synthesized sound — sparkler crackle (7→1s) + BOOM at 0s. All web
  // audio; no files. Auto-unlocks on the first user gesture.
  useFireSound({ burning, secondsLeft, muted });

  return (
    <span
      className={`relative inline-flex isolate ${className}`}
      data-testid="fire-pill"
      data-burning={burning ? "true" : "false"}
    >
      {/* Sparks render BEHIND the button (z-0). The button sits on top
          (z-10) so sparks visibly emerge from behind it. */}
      {burning && <SparkCanvas intensity={intensity} explodeKey={explodeKey} />}
      <span
        className="relative z-10 inline-flex"
        style={
          shaking
            ? {
                animation: hardShake
                  ? "fire-shake 0.12s ease-in-out infinite"
                  : "fire-shake 0.22s ease-in-out infinite",
              }
            : undefined
        }
      >
        {children}
      </span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Particle system — golden sparks fanning out from behind the button.
// ─────────────────────────────────────────────────────────────────────────
const BLEED = 130; // canvas extends this many px on every side of the button

function SparkCanvas({ intensity, explodeKey }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const intensityRef = useRef(intensity);
  intensityRef.current = intensity;

  // Each new value of `explodeKey` (e.g. it transitions from null → 1)
  // queues a one-shot massive radial burst of ~140 high-velocity sparks
  // — the "firework explosion" moment at the 1-second mark.
  const explodePendingRef = useRef(false);
  useEffect(() => {
    if (explodeKey != null) explodePendingRef.current = true;
  }, [explodeKey]);

  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const measure = () => {
      const r = wrap.parentElement.getBoundingClientRect();
      setSize({
        w: Math.max(40, Math.floor(r.width + BLEED * 2)),
        h: Math.max(40, Math.floor(r.height + BLEED * 2)),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrap.parentElement) ro.observe(wrap.parentElement);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.w === 0 || size.h === 0) return undefined;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = size.w * dpr;
    canvas.height = size.h * dpr;
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    /** Each spark stores prevX/prevY so we can draw a short motion-blur
     *  trail by stroking a line from prev → current each frame. That's
     *  what gives the "shooting tail" sparkler look. */
    const sparks = [];

    // Origin = dead center of the button (which itself sits centered in
    // the canvas thanks to BLEED on all sides).
    const ox = size.w / 2;
    const oy = size.h / 2;

    let raf = 0;
    let lastT = performance.now();

    const tick = (now) => {
      const dt = Math.min(48, now - lastT) / 1000;
      lastT = now;
      const I = intensityRef.current;

      // Spawn rate scales with intensity. Steady stream + occasional
      // bursts make it feel alive instead of metronomic.
      const baseSpawn = 5 + I * 8;
      const burstChance = 0.18 + I * 0.25;
      const nSpawn = Math.round(baseSpawn + (Math.random() < burstChance ? 6 + Math.random() * 6 : 0));
      for (let i = 0; i < nSpawn; i++) {
        // Fan outward in all directions, but slightly bias UP (negative Y)
        // and the sides — that's the firework pop look. Pure 2π would
        // bury half the sparks behind the button.
        const angle = Math.random() * Math.PI * 2;
        // Speed varies a lot — some sparks travel fast (long arcs), most
        // are short hops. Quadratic distribution gives the right mix.
        const speed = 60 + Math.pow(Math.random(), 2) * (240 + I * 120);
        const upBias = -28 - I * 14; // global upward push
        sparks.push({
          x: ox + (Math.random() - 0.5) * 8,
          y: oy + (Math.random() - 0.5) * 4,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed + upBias,
          age: 0,
          life: 0.55 + Math.random() * 0.55 + I * 0.15,
          radius: 0.9 + Math.random() * 1.1,
          // Color jitter — most golden, a few warmer (amber/red),
          // a few cooler (white-yellow). Reads as natural sparkler mix.
          tone: Math.random(),
        });
      }

      // ── Explosion at the 0-second mark ──────────────────────────────
      // One-shot massive radial burst — 320 sparks shooting hard in
      // every direction in two layered rings (slow inner + fast outer)
      // for the chunky firework "pop" feel. Fired from the same origin
      // so it looks like a real firework popping out from behind the
      // button.
      if (explodePendingRef.current) {
        explodePendingRef.current = false;
        // Inner ring — slower, fatter, white-hot. The flash core.
        const innerN = 120;
        for (let i = 0; i < innerN; i++) {
          const angle = (i / innerN) * Math.PI * 2 + (Math.random() - 0.5) * 0.35;
          const speed = 160 + Math.random() * 220;
          sparks.push({
            x: ox,
            y: oy,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            age: 0,
            life: 1.1 + Math.random() * 0.6,
            radius: 1.8 + Math.random() * 1.6,
            tone: Math.random() * 0.35, // skew bright/white-hot
          });
        }
        // Outer ring — faster, thinner sparks shooting out further.
        // These reach the edges of the canvas and form the firework's
        // outer halo.
        const outerN = 200;
        for (let i = 0; i < outerN; i++) {
          const angle = (i / outerN) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
          const speed = 360 + Math.random() * 480;
          sparks.push({
            x: ox,
            y: oy,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            age: 0,
            life: 1.2 + Math.random() * 0.9,
            radius: 1.2 + Math.random() * 1.3,
            tone: Math.random() * 0.7, // mostly gold w/ some warm
          });
        }
      }

      ctx.clearRect(0, 0, size.w, size.h);
      ctx.globalCompositeOperation = "lighter";

      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.age += dt;
        if (s.age >= s.life) {
          sparks.splice(i, 1);
          continue;
        }
        // Save previous position for trail drawing.
        const px = s.x;
        const py = s.y;
        // Gravity — sparks arc downward as they slow.
        s.vy += 280 * dt;
        // Air drag — gives the natural decelerating feel of sparks.
        const drag = Math.pow(0.86, dt * 60);
        s.vx *= drag;
        s.vy = (s.vy - 280 * dt) * drag + 280 * dt; // re-apply gravity post-drag
        s.x += s.vx * dt;
        s.y += s.vy * dt;

        const lf = s.age / s.life;
        const a = (1 - lf) * 0.95;

        // Pick the spark color — golden default, with occasional
        // warmer/cooler accents. Gradient core → tail.
        let coreColor;
        let tailColor;
        if (s.tone < 0.08) {
          // White-hot
          coreColor = `rgba(255, 255, 240, ${a})`;
          tailColor = `rgba(255, 230, 130, ${a * 0.7})`;
        } else if (s.tone < 0.85) {
          // Classic gold (the dominant color)
          coreColor = `rgba(255, 220, 110, ${a})`;
          tailColor = `rgba(255, 170, 50, ${a * 0.65})`;
        } else {
          // Warm amber
          coreColor = `rgba(255, 160, 40, ${a})`;
          tailColor = `rgba(220, 90, 20, ${a * 0.6})`;
        }

        // Motion-blur trail — stroke a line from prev to current with a
        // soft tail color. Length naturally reflects velocity.
        ctx.strokeStyle = tailColor;
        ctx.lineWidth = s.radius * 1.2;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();

        // Glowing core dot at the leading edge.
        const r = s.radius * (1 - lf * 0.4);
        const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, r * 4);
        grad.addColorStop(0, coreColor);
        grad.addColorStop(0.4, tailColor);
        grad.addColorStop(1, "rgba(255, 140, 30, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(s.x, s.y, r * 4, 0, Math.PI * 2);
        ctx.fill();
        // Hard core
        ctx.fillStyle = coreColor;
        ctx.beginPath();
        ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [size]);

  return (
    <span
      ref={wrapRef}
      className="pointer-events-none absolute z-0"
      aria-hidden="true"
      style={{
        left: -BLEED,
        right: -BLEED,
        top: -BLEED,
        bottom: -BLEED,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
        }}
      />
    </span>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Synthesized fire/firework audio. Two sounds, both generated in real-time
// by the WebAudio API — zero audio files, ~2KB extra DOM.
//
//   • Sparkler crackle: continuous low-volume bandpassed white-noise pops
//     at random ~30–110ms intervals during 7→1s. Each pop is ~12ms long
//     with a quick exponential decay → reads as a real sparkler hiss.
//
//   • BOOM (~0.4s): three-layer synth pop fired exactly once on the
//     0-second transition:
//       1. Sub-bass sine sweep (80→30 Hz) for the gut-punch thump.
//       2. Bandpassed white-noise burst at 1500 Hz for the firework "pop".
//       3. High-passed crackle tail at 5kHz for the sparkler shimmer.
//
// Browsers block AudioContext until a user gesture; we lazy-create the
// context and call `.resume()` on first invocation. If `muted` is true
// we never create the context.
// ─────────────────────────────────────────────────────────────────────────
let sharedAudioCtx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  if (sharedAudioCtx) return sharedAudioCtx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  sharedAudioCtx = new Ctor();
  // Best-effort unlock — works once the surrounding click bubbles up.
  // If still suspended we'll try again on each tick.
  if (sharedAudioCtx.state === "suspended") {
    const resume = () => sharedAudioCtx?.resume?.();
    window.addEventListener("pointerdown", resume, { once: true, passive: true });
    window.addEventListener("touchstart", resume, { once: true, passive: true });
    window.addEventListener("keydown", resume, { once: true, passive: true });
  }
  return sharedAudioCtx;
}

function playSparklerCrackle(ctx, gain = 0.045) {
  const dur = 0.012;
  const sr = ctx.sampleRate;
  const buf = ctx.createBuffer(1, Math.max(1, Math.floor(sr * dur)), sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    const env = Math.pow(1 - i / data.length, 2);
    data[i] = (Math.random() * 2 - 1) * env;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = 4500;
  const g = ctx.createGain();
  g.gain.value = gain;
  src.connect(filter).connect(g).connect(ctx.destination);
  src.start();
}

function playBoom(ctx) {
  const now = ctx.currentTime;

  // 1) Sub-bass thump — 80 Hz → 30 Hz exponential sweep.
  const subOsc = ctx.createOscillator();
  subOsc.type = "sine";
  subOsc.frequency.setValueAtTime(80, now);
  subOsc.frequency.exponentialRampToValueAtTime(30, now + 0.32);
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0, now);
  subGain.gain.linearRampToValueAtTime(0.55, now + 0.005);
  subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.42);
  subOsc.connect(subGain).connect(ctx.destination);
  subOsc.start(now);
  subOsc.stop(now + 0.45);

  // 2) White-noise burst — bandpassed for the firework "pop".
  const dur = 0.4;
  const sr = ctx.sampleRate;
  const buf = ctx.createBuffer(1, Math.floor(sr * dur), sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    const env = Math.pow(1 - i / data.length, 1.6);
    data[i] = (Math.random() * 2 - 1) * env;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = "bandpass";
  noiseFilter.frequency.value = 1400;
  noiseFilter.Q.value = 0.7;
  const noiseGain = ctx.createGain();
  noiseGain.gain.value = 0.32;
  noise.connect(noiseFilter).connect(noiseGain).connect(ctx.destination);
  noise.start(now);

  // 3) Bright crackle tail — high-passed sparkle right after the thump.
  const crackDur = 0.28;
  const crackBuf = ctx.createBuffer(1, Math.floor(sr * crackDur), sr);
  const crackData = crackBuf.getChannelData(0);
  for (let i = 0; i < crackData.length; i++) {
    crackData[i] = (Math.random() * 2 - 1) * (Math.random() < 0.4 ? 1 : 0);
  }
  const crack = ctx.createBufferSource();
  crack.buffer = crackBuf;
  const crackFilter = ctx.createBiquadFilter();
  crackFilter.type = "highpass";
  crackFilter.frequency.value = 5000;
  const crackGain = ctx.createGain();
  crackGain.gain.setValueAtTime(0, now + 0.04);
  crackGain.gain.linearRampToValueAtTime(0.16, now + 0.1);
  crackGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
  crack.connect(crackFilter).connect(crackGain).connect(ctx.destination);
  crack.start(now + 0.04);
}

function useFireSound({ burning, secondsLeft, muted }) {
  // Sparkler loop — runs while burning && secondsLeft > 0.
  const sparklerActive = burning && !muted && secondsLeft > 0;
  useEffect(() => {
    if (!sparklerActive) return undefined;
    let alive = true;
    let timeoutId = null;
    const tick = () => {
      if (!alive) return;
      const ctx = getAudioCtx();
      if (ctx && ctx.state === "running") {
        playSparklerCrackle(ctx);
      }
      const next = 28 + Math.random() * 90;
      timeoutId = setTimeout(tick, next);
    };
    tick();
    return () => {
      alive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sparklerActive]);

  // BOOM — fires exactly once when secondsLeft transitions to 0 while
  // burning. We track the previous value to avoid retriggering on every
  // re-render at 0s.
  const prevSecondsRef = useRef(null);
  useEffect(() => {
    const prev = prevSecondsRef.current;
    prevSecondsRef.current = burning ? secondsLeft : null;
    if (muted) return;
    if (!burning || secondsLeft !== 0 || prev === 0) return;
    const ctx = getAudioCtx();
    if (!ctx) return;
    // If still suspended (no gesture yet), the resume listeners on first
    // pointerdown/touchstart will fix it for next time.
    if (ctx.state !== "running") {
      ctx.resume?.().then(() => playBoom(ctx)).catch(() => {});
      return;
    }
    playBoom(ctx);
  }, [burning, secondsLeft, muted]);
}
